import { useCallback, useEffect, useRef, useState } from "react";
import { fetchHaStates } from "../ha";

const POLL_MS = 5000;
// Après une commande locale, la valeur affichée est conservée ce temps-là
// (évite que l'interrupteur "saute" en arrière avant que HA ait pris l'état en compte)
const HOLD_MS = 4000;
const SPA_TEMP_ID = "sensor.esp_spa_temp_temp_rature_spa";
const MEDIA_ID = "media_player.n2mini";

const isHeld = (holds, key) => (holds[key] || 0) > Date.now();
const hold = (holds, key) => { holds[key] = Date.now() + HOLD_MS; };

// Synchronise l'état des appareils avec Home Assistant (via haProxy) :
// - pas de requêtes qui se chevauchent, pause quand l'onglet est masqué
// - pas de re-rendu si rien n'a changé
// - les valeurs modifiées localement ne sont pas écrasées pendant HOLD_MS
export function useHaStates({ enabled, sejourToken, entityIds, onAccessDenied }) {
  const [deviceStates, setDeviceStates] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [spaTemp, setSpaTemp] = useState(null);
  const [volume, setVolumeState] = useState(0.5);
  const [isMuted, setIsMutedState] = useState(false);
  const [haOnline, setHaOnline] = useState(true);

  const holds = useRef({});
  const failures = useRef(0);
  const inFlight = useRef(false);
  const scheduleRef = useRef(null);
  const deniedRef = useRef(onAccessDenied);
  deniedRef.current = onAccessDenied;


  const refresh = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const states = await fetchHaStates(sejourToken);
      const byId = {};
      for (const s of states) byId[s.entity_id] = s;
      failures.current = 0;
      setHaOnline(true);

      setDeviceStates((prev) => {
        let changed = false;
        const next = { ...prev };
        for (const id of entityIds) {
          const found = byId[id];
          if (!found || isHeld(holds.current, id)) continue;
          const on = found.state === "on";
          if (next[id] !== on) { next[id] = on; changed = true; }
        }
        return changed ? next : prev;
      });

      const temp = parseFloat(byId[SPA_TEMP_ID]?.state);
      if (Number.isFinite(temp)) setSpaTemp(temp);

      const media = byId[MEDIA_ID]?.attributes;
      if (media) {
        if (media.volume_level !== undefined && !isHeld(holds.current, "volume")) setVolumeState(media.volume_level);
        if (media.is_volume_muted !== undefined && !isHeld(holds.current, "mute")) setIsMutedState(media.is_volume_muted);
      }
      setLoaded(true);
    } catch (err) {
      if (err.code === "hors_dates" || err.code === "lien_invalide") {
        deniedRef.current?.();
        return;
      }
      console.error("Erreur actualisation états domotiques :", err);
      // Deux échecs consécutifs avant d'afficher "hors ligne" (évite les fausses alertes)
      failures.current += 1;
      if (failures.current >= 2) setHaOnline(false);
    } finally {
      inFlight.current = false;
    }
  }, [sejourToken, entityIds]);

  useEffect(() => {
    if (!enabled) return undefined;
    let stopped = false;
    let timer = null;

    // Un seul minuteur actif à la fois : chaque planification remplace la précédente
    const schedule = (ms) => {
      clearTimeout(timer);
      timer = setTimeout(tick, ms);
    };
    const tick = async () => {
      if (stopped || document.hidden) return; // reprend au retour sur l'onglet
      await refresh();
      if (!stopped) schedule(POLL_MS);
    };
    const onVisibility = () => { if (!document.hidden) schedule(0); };

    scheduleRef.current = schedule;
    schedule(0);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stopped = true;
      clearTimeout(timer);
      scheduleRef.current = null;
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled, refresh]);

  // Mise à jour optimiste d'un appareil (l'état réel est relu ensuite)
  const setDeviceLocal = useCallback((id, value) => {
    hold(holds.current, id);
    setDeviceStates((prev) => ({ ...prev, [id]: value }));
  }, []);

  const setVolume = useCallback((v) => { hold(holds.current, "volume"); setVolumeState(v); }, []);
  const setIsMuted = useCallback((m) => { hold(holds.current, "mute"); setIsMutedState(m); }, []);

  // Relit l'état réel juste après la fin de la période de maintien
  const refreshSoon = useCallback(() => { scheduleRef.current?.(HOLD_MS + 200); }, []);

  return { deviceStates, loaded, spaTemp, volume, setVolume, isMuted, setIsMuted, haOnline, setDeviceLocal, refreshSoon };
}
